import AggrTypes from "../../../queries/technogroup-result-details/AggrTypes";
import DataTypes from "../../../queries/technogroup-result-details/DataTypes";

/**
 * Nadaje każdemu nagłówkowi stabilny klucz (`columnKey`), na podstawie którego zapamiętywana jest widoczność.
 * Usuwa puste `children`, więc dalej grupa to zawsze nagłówek z co najmniej jednym dzieckiem.
 * Kolejność wyboru: header.key -> header.field -> header.name. Duplikaty dostają sufiks "#n".
 * @param {Array} headers
 * @returns {Array} kopia drzewa nagłówków z polem `columnKey`
 */
export function withColumnKeys(headers) {
    const used = {};
    const assign = (header, path) => {
        const base = String(
            header.key ?? header.field ?? header.name ?? path.join(".")
        );
        const count = used[base] ?? 0;
        used[base] = count + 1;
        const columnKey = count === 0 ? base : `${base}#${count}`;
        if (header.children?.length > 0) {
            return {
                ...header,
                columnKey,
                children: header.children.map((child, index) =>
                    assign(child, [...path, index])
                ),
            };
        }
        // pusta lub null-owa lista dzieci (np. useDashboardDetails) to zwykła kolumna
        const {children, ...leaf} = header;
        return {...leaf, columnKey};
    };
    return headers.map((header, index) => assign(header, [index]));
}

/**
 * Zwraca klucze wszystkich liści (kolumn z danymi) w drzewie nagłówków.
 * @param {Array} headers drzewo z `columnKey`
 * @returns {string[]}
 */
export function getLeafKeys(headers) {
    const keys = [];
    const walk = (header) => {
        if (header.children) header.children.forEach(walk);
        else keys.push(header.columnKey);
    };
    headers.forEach(walk);
    return keys;
}

/**
 * Domyślnie ukryte kolumny - te z `hidden: true` w definicji nagłówka.
 * @param {Array} headers drzewo z `columnKey`
 * @returns {string[]}
 */
export function getDefaultHiddenKeys(headers) {
    const keys = [];
    const walk = (header) => {
        if (header.children) header.children.forEach(walk);
        else if (header.hidden) keys.push(header.columnKey);
    };
    headers.forEach(walk);
    return keys;
}

/**
 * Usuwa z drzewa ukryte kolumny oraz grupy bez żadnej widocznej kolumny (także grupy puste od początku).
 * @param {Array} headers drzewo z `columnKey`
 * @param {string[]} hiddenKeys
 * @returns {Array}
 */
export function filterHiddenHeaders(headers, hiddenKeys) {
    // bez wczesnego wyjścia przy braku ukrytych kolumn - pusta grupa (nowa grupa użytkownika)
    // też musi wypaść, inaczej colgroup i nagłówki tabeli mają różną liczbę kolumn
    const hidden = new Set(hiddenKeys ?? []);
    const filter = (items) =>
        items.reduce((result, header) => {
            if (header.children) {
                const children = filter(header.children);
                if (children.length > 0) result.push({...header, children});
            } else if (!hidden.has(header.columnKey)) {
                result.push(header);
            }
            return result;
        }, []);
    return filter(headers);
}

/**
 * Zwraca klucze wszystkich nagłówków (grup i kolumn) w kolejności z drzewa.
 * @param {Array} headers drzewo z `columnKey`
 * @returns {string[]}
 */
export function getAllKeys(headers) {
    const keys = [];
    const walk = (header) => {
        keys.push(header.columnKey);
        if (header.children) header.children.forEach(walk);
    };
    headers.forEach(walk);
    return keys;
}

/**
 * Układa nagłówki według zapisanej kolejności. Kolejność działa w obrębie rodzeństwa,
 * więc kolumna nigdy nie wychodzi ze swojej grupy. Nagłówki spoza `order` zostają na końcu w oryginalnej kolejności.
 * @param {Array} headers drzewo z `columnKey`
 * @param {string[]} order
 * @returns {Array}
 */
export function applyColumnOrder(headers, order) {
    if (!order || order.length === 0) return headers;
    const position = new Map(order.map((key, index) => [key, index]));
    const sortLevel = (items) =>
        items
            .map((header, index) => ({
                header: header.children
                    ? {...header, children: sortLevel(header.children)}
                    : header,
                rank: position.has(header.columnKey)
                    ? position.get(header.columnKey)
                    : order.length + index,
            }))
            .sort((a, b) => a.rank - b.rank)
            .map((item) => item.header);
    return sortLevel(headers);
}

const NUMBER_AGGREGATES = [
    AggrTypes.SUM,
    AggrTypes.AVERAGE,
    AggrTypes.WEIGHTED_AVERAGE,
    AggrTypes.MIN,
    AggrTypes.MAX,
    AggrTypes.COUNT_DISTINCT,
    AggrTypes.MOST_COMMON,
];

const DATE_AGGREGATES = [
    AggrTypes.MIN,
    AggrTypes.MAX,
    AggrTypes.COUNT_DISTINCT,
];

const TEXT_AGGREGATES = [AggrTypes.COUNT_DISTINCT, AggrTypes.MOST_COMMON];

/**
 * Agregaty, które można wybrać dla kolumny. Lista zależy od typu danych (`header.type`),
 * można ją też podać wprost w `header.aggregateTypes`.
 * Kolumna bez typu dostaje agregaty liczbowe tylko wtedy, gdy ma domyślny `aggregate`.
 * Średnia ważona wymaga kolumny wagi (`aggregateOptions.countKey`).
 * @param {Object} header
 * @returns {string[]|null} lista typów lub null, gdy kolumny nie da się agregować
 */
export function getAggregateTypes(header) {
    let types = header.aggregateTypes;
    if (!types) {
        switch (header.type) {
            case DataTypes.NUMBER:
            case DataTypes.WEIGHT:
                types = NUMBER_AGGREGATES;
                break;
            case DataTypes.DATE:
                types = DATE_AGGREGATES;
                break;
            case DataTypes.STRING:
            case DataTypes.MONTH:
            case DataTypes.WEEK_DAY:
                types = TEXT_AGGREGATES;
                break;
            default:
                types = header.aggregate ? NUMBER_AGGREGATES : null;
        }
    }
    if (!types) return null;
    // wybór wielokrotny - brak agregatu to pusta lista, więc NONE nie jest opcją
    return types.filter(
        (type) =>
            type !== AggrTypes.NONE &&
            (type !== AggrTypes.WEIGHTED_AVERAGE ||
                !!header.aggregateOptions?.countKey)
    );
}

/**
 * Agregaty kolumny jako lista. Starsze ustawienia i definicje nagłówków trzymają jeden typ (string),
 * a `NONE` oznacza brak agregatu.
 * @param {string|string[]|undefined} value
 * @returns {string[]}
 */
export function normalizeAggregate(value) {
    if (value === undefined || value === null) return [];
    const list = Array.isArray(value) ? value : [value];
    return list.filter((type) => type && type !== AggrTypes.NONE);
}

/**
 * Podmienia agregaty kolumn na wybrane przez użytkownika (lista typów; pusta - brak agregatu).
 * @param {Array} headers drzewo z `columnKey`
 * @param {Object<string, string>} aggregates columnKey -> typ agregatu
 * @returns {Array}
 */
export function applyAggregates(headers, aggregates) {
    if (!aggregates || Object.keys(aggregates).length === 0) return headers;
    const apply = (header) => {
        if (header.children) {
            return {...header, children: header.children.map(apply)};
        }
        if (!(header.columnKey in aggregates)) return header;
        const aggregate = normalizeAggregate(aggregates[header.columnKey]);
        return {
            ...header,
            aggregate: aggregate.length > 0 ? aggregate : undefined,
        };
    };
    return headers.map(apply);
}

/**
 * Szuka kolumny (liścia) po kluczu.
 * @param {Array} headers drzewo z `columnKey`
 * @param {string} columnKey
 * @returns {Object|null}
 */
export function findColumn(headers, columnKey) {
    if (!columnKey) return null;
    for (const header of headers) {
        if (header.children) {
            const found = findColumn(header.children, columnKey);
            if (found) return found;
        } else if (header.columnKey === columnKey) {
            return header;
        }
    }
    return null;
}

/**
 * Poziomy grupowania z ustawień jako lista kluczy. Starsze ustawienia trzymały jeden klucz (string).
 * @param {string|string[]|undefined} groupBy
 * @returns {string[]}
 */
export function normalizeGroupBy(groupBy) {
    if (!groupBy) return [];
    return Array.isArray(groupBy) ? groupBy : [groupBy];
}

/**
 * Usuwa z drzewa podane kolumny oraz grupy nagłówków, w których nic nie zostało.
 * @param {Array} headers drzewo z `columnKey`
 * @param {string[]} keys klucze kolumn do usunięcia
 * @returns {Array}
 */
export function removeColumns(headers, keys) {
    if (!keys || keys.length === 0) return headers;
    const removed = new Set(keys);
    const strip = (items) =>
        items.reduce((result, header) => {
            if (header.children) {
                const children = strip(header.children);
                // grupa pusta od początku (nowa grupa użytkownika) zostaje - pusta po usunięciu znika
                if (children.length > 0 || header.children.length === 0) {
                    result.push({...header, children});
                }
            } else if (!removed.has(header.columnKey)) {
                result.push(header);
            }
            return result;
        }, []);
    return strip(headers);
}

/**
 * Tryb grupowania: kolumny poziomów grupowania stają na początku tabeli, w kolejności poziomów - każda z własnym
 * nagłówkiem, filtrem i sortowaniem. Wiersz grupy pokazuje etykietę w kolumnie swojego poziomu (`groupLevel`),
 * a wiersz danych - wartości we wszystkich kolumnach poziomów (`cellClassName`: linia zagnieżdżenia; wartość
 * poziomu "Każda wartość" przygaszona, bo powtarza klucz grupy - poziom z regułą ma różne wartości w grupie).
 * Grupa nagłówków, w której nic nie zostało, znika.
 * @param {Array} headers drzewo z `columnKey` (już przefiltrowane z ukrytych)
 * @param {Array} groupColumns kolumny grupowania w kolejności poziomów
 * @param {string[]} [ruleKeys] columnKey poziomów z regułą
 * @returns {Array}
 */
export function buildGroupLevelHeaders(headers, groupColumns, ruleKeys = []) {
    if (!groupColumns || groupColumns.length === 0) return headers;
    // z bieżącego drzewa (agregaty, formatowanie); ukryta w ustawieniach i tak stoi - etykiety grup są w niej
    const levelColumns = groupColumns.map((column, groupLevel) => ({
        ...(findColumn(headers, column.columnKey) ?? column),
        groupLevel,
        groupRuleLevel: ruleKeys.includes(column.columnKey),
        cellClassName: ruleKeys.includes(column.columnKey)
            ? "async-table-group-ancestor"
            : "async-table-group-ancestor async-table-group-repeated",
    }));
    return [
        ...levelColumns,
        ...removeColumns(
            headers,
            groupColumns.map((column) => column.columnKey)
        ),
    ];
}

/*
 * Układ kolumn użytkownika (grupy kolumn tworzone w ustawieniach). Zestaw kolumn jest stały - układ mówi tylko,
 * w jakiej kolejności stoją i do której grupy (jednopoziomowej) należą:
 *   [{key: "AnmNo1"}, {group: "Feeding", name?: "Karmienie", columns: ["eaten", "plan"]}, ...]
 * Grupa domyślna (z definicji tabeli) ma id = columnKey nagłówka i bez `name` bierze nazwę z definicji.
 */

/**
 * Układ z bieżącego drzewa nagłówków. Zagnieżdżone grupy spłaszczają się do grupy najwyższego poziomu.
 * @param {Array} headers drzewo z `columnKey`
 * @returns {Array}
 */
export function layoutFromHeaders(headers) {
    return headers.map((header) =>
        header.children
            ? {
                  group: header.columnKey,
                  ...(header.customName ? {name: header.customName} : {}),
                  columns: getLeafKeys(header.children),
              }
            : {key: header.columnKey}
    );
}

/**
 * Drzewo nagłówków według układu. Kolumny spoza układu (np. dodane później do tabeli) trafiają na koniec,
 * a puste grupy zostają w drzewie (lista w ustawieniach je pokazuje, tabela pomija).
 * @param {Array} headers drzewo z `columnKey`
 * @param {Array|null} layout
 * @returns {Array}
 */
export function applyLayout(headers, layout) {
    if (!layout) return headers;
    const leaves = new Map();
    const collect = (items) =>
        items.forEach((header) =>
            header.children
                ? collect(header.children)
                : leaves.set(header.columnKey, header)
        );
    collect(headers);
    const defaultGroups = new Map(
        headers
            .filter((header) => header.children)
            .map((header) => [header.columnKey, header])
    );
    const used = new Set();
    const take = (key) => {
        const leaf = leaves.get(key);
        if (!leaf || used.has(key)) return null;
        used.add(key);
        return leaf;
    };
    const result = layout
        .map((item) => {
            if (item.group === undefined) return take(item.key);
            const base = defaultGroups.get(item.group) ?? {};
            return {
                ...base,
                columnKey: item.group,
                name: item.name ?? base.name ?? "",
                customName: item.name,
                isCustomGroup: true,
                children: item.columns.map(take).filter(Boolean),
            };
        })
        .filter(Boolean);
    for (const [key, leaf] of leaves) {
        if (!used.has(key)) result.push(leaf);
    }
    return result;
}

const isGroupItem = (item) => item.group !== undefined;

const itemKey = (item) => (isGroupItem(item) ? item.group : item.key);

// położenie elementu w układzie: {group: id|null, index}
function locate(layout, key) {
    for (let index = 0; index < layout.length; index++) {
        const item = layout[index];
        if (itemKey(item) === key) return {group: null, index};
        if (isGroupItem(item)) {
            const inner = item.columns.indexOf(key);
            if (inner !== -1) return {group: item.group, index: inner};
        }
    }
    return null;
}

function removeKey(layout, key) {
    return layout
        .filter((item) => isGroupItem(item) || item.key !== key)
        .map((item) =>
            isGroupItem(item) && item.columns.includes(key)
                ? {...item, columns: item.columns.filter((k) => k !== key)}
                : item
        );
}

function arrayMove(list, from, to) {
    const copy = list.slice();
    copy.splice(to, 0, copy.splice(from, 1)[0]);
    return copy;
}

/**
 * Przenosi kolumnę w układzie.
 * - `overKey` - element, na który upuszczono (kolumna albo grupa najwyższego poziomu),
 * - `into` - kolumna trafia na koniec grupy `overKey`,
 * - `toRoot` - kolumna z grupy wychodzi na poziom grup, zaraz za tę grupę.
 * W tej samej liście działa jak arrayMove, między listami wstawia przed `overKey` (`after` - za nim).
 * @returns {Array} nowy układ (ten sam obiekt, gdy przeniesienie nic nie zmienia)
 */
export function layoutMoveColumn(
    layout,
    key,
    overKey,
    {into = false, toRoot = false, after = false} = {}
) {
    const from = locate(layout, key);
    const over = locate(layout, overKey);
    if (!from || !over || (key === overKey && !toRoot)) return layout;

    if (into) {
        return removeKey(layout, key).map((item) =>
            item.group === overKey
                ? {...item, columns: [...item.columns, key]}
                : item
        );
    }

    if (toRoot && over.group !== null) {
        // za grupę, w której jest element docelowy
        const without = removeKey(layout, key);
        const index =
            without.findIndex((item) => item.group === over.group) + 1;
        const copy = without.slice();
        copy.splice(index, 0, {key});
        return copy;
    }

    if (over.group === from.group) {
        if (from.group === null)
            return arrayMove(layout, from.index, over.index);
        return layout.map((item) =>
            item.group === from.group
                ? {
                      ...item,
                      columns: arrayMove(item.columns, from.index, over.index),
                  }
                : item
        );
    }

    // inna lista - wstawiamy przed element docelowy (`after` - za nim)
    const shift = after ? 1 : 0;
    const without = removeKey(layout, key);
    if (over.group === null) {
        const index = without.findIndex((item) => itemKey(item) === overKey);
        const copy = without.slice();
        copy.splice(index + shift, 0, {key});
        return copy;
    }
    return without.map((item) => {
        if (item.group !== over.group) return item;
        const columns = item.columns.slice();
        columns.splice(columns.indexOf(overKey) + shift, 0, key);
        return {...item, columns};
    });
}

/**
 * Przenosi grupę (całość) na miejsce elementu najwyższego poziomu `overKey`.
 * Gdy `overKey` jest kolumną w grupie, celem jest ta grupa.
 */
export function layoutMoveGroup(layout, groupId, overKey) {
    const from = locate(layout, groupId);
    const over = locate(layout, overKey);
    if (!from || !over) return layout;
    const to =
        over.group === null ? over.index : locate(layout, over.group).index;
    if (from.index === to) return layout;
    return arrayMove(layout, from.index, to);
}

/**
 * Dodaje pustą grupę za ostatnią grupą (kolumny bez grupy zostają na końcu).
 */
export function layoutAddGroup(layout, groupId, name) {
    let index = 0;
    layout.forEach((item, i) => {
        if (isGroupItem(item)) index = i + 1;
    });
    const copy = layout.slice();
    copy.splice(index, 0, {group: groupId, name, columns: []});
    return copy;
}

export function layoutRenameGroup(layout, groupId, name) {
    return layout.map((item) =>
        item.group === groupId ? {...item, name} : item
    );
}

/**
 * Usuwa grupę - jej kolumny zostają i przechodzą na poziom grup w miejscu grupy.
 */
export function layoutRemoveGroup(layout, groupId) {
    return layout.flatMap((item) =>
        item.group === groupId ? item.columns.map((key) => ({key})) : [item]
    );
}

/**
 * Przenosi kolumnę w drzewie nagłówków (robocza kopia listy w ustawieniach podczas przeciągania między grupami).
 * @param {Array} headers drzewo z `columnKey`
 * @param {string} key przenoszona kolumna
 * @param {string} containerKey "root" (poziom grup) albo columnKey grupy
 * @param {string|null} beforeKey wstaw przed tym elementem; null - na koniec listy
 * @returns {Array}
 */
export function moveHeader(headers, key, containerKey, beforeKey) {
    let moving = null;
    const strip = (items) =>
        items.reduce((result, header) => {
            if (!header.children && header.columnKey === key) {
                moving = header;
                return result;
            }
            result.push(
                header.children
                    ? {...header, children: strip(header.children)}
                    : header
            );
            return result;
        }, []);
    const stripped = strip(headers);
    if (!moving) return headers;
    const insert = (items) => {
        const index = beforeKey
            ? items.findIndex((header) => header.columnKey === beforeKey)
            : -1;
        const copy = items.slice();
        copy.splice(index === -1 ? copy.length : index, 0, moving);
        return copy;
    };
    if (containerKey === "root") return insert(stripped);
    const walk = (header) => {
        if (!header.children) return header;
        if (header.columnKey === containerKey) {
            return {...header, children: insert(header.children)};
        }
        return {...header, children: header.children.map(walk)};
    };
    return stripped.map(walk);
}

/**
 * Lista, w której stoi kolumna: {containerKey, list, index}.
 * @param {Array} headers drzewo z `columnKey`
 * @param {string} key
 */
export function findHeaderContainer(headers, key, containerKey = "root") {
    const index = headers.findIndex((header) => header.columnKey === key);
    if (index !== -1) return {containerKey, list: headers, index};
    for (const header of headers) {
        if (header.children) {
            const found = findHeaderContainer(
                header.children,
                key,
                header.columnKey
            );
            if (found) return found;
        }
    }
    return null;
}
